import { useEffect, useState, useCallback } from "react";
import { listConversations } from "../api/conversations";
import type { ConversationSummary } from "../api/types";

type Props = {
  projectId: string;
  projectName?: string;
  expanded: boolean;
  onSelectConversation: (conversationId: number) => void;
  refreshKey?: number;
};

const MAX_ITEMS = 20;

function fmtAgo(tsMs: number | null | undefined): string {
  if (!tsMs) return "";
  const nowMs = Date.now();
  const diffSec = Math.max(0, Math.floor((nowMs - tsMs) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMo = Math.floor(diffDay / 30);
  if (diffMo < 12) return `${diffMo}mo ago`;
  return `${Math.floor(diffMo / 12)}y ago`;
}

function previewText(c: ConversationSummary): string {
  const p = (c.preview ?? "").trim();
  if (p.length > 0) return p;
  return "(empty conversation)";
}

export default function ConversationDrawer({
  projectId,
  projectName,
  expanded,
  onSelectConversation,
  refreshKey,
}: Props) {
  const [convs, setConvs] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const list = await listConversations(projectId);
      setConvs(list.slice(0, MAX_ITEMS));
    } catch (e) {
      setErr((e as Error).message || "failed to load");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!expanded) return;
    void load();
  }, [expanded, refreshKey, load]);

  if (!expanded) return null;

  return (
    <div className="conversation-drawer">
      {loading && (
        <div className="conversation-skeleton" aria-label="Loading conversations" role="status">
          <div className="conversation-skeleton-row" />
          <div className="conversation-skeleton-row" style={{ width: "85%" }} />
          <div className="conversation-skeleton-row" style={{ width: "70%" }} />
        </div>
      )}
      {!loading && err && (
        <div className="conversation-error">
          <span>Error: {err}</span>
          <button
            type="button"
            className="conversation-retry"
            onClick={() => { void load(); }}
          >
            Retry
          </button>
        </div>
      )}
      {!loading && !err && convs.length === 0 && (
        <div className="conversation-empty">
          {projectName
            ? `No conversations yet for ${projectName}. Send your first message to start one.`
            : "No conversations yet. Send your first message to start one."}
        </div>
      )}
      {!loading && !err && convs.length > 0 && (
        <ul className="conversation-list">
          {convs.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="conversation-row"
                onClick={() => onSelectConversation(c.id)}
                title={previewText(c)}
              >
                <span className="conversation-preview">{previewText(c)}</span>
                <span className="conversation-meta">
                  {c.turn_count}t · {fmtAgo(c.last_turn_at ?? c.created_at)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
